import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, Link2, Mail, Check } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../../services/apiClient';
import SongSectionsPicker, { DEFAULT_CAPS } from './SongSectionsPicker';

/**
 * AdminSongSections — the admin Planning-tab surface for the SHARED song list.
 * Reads the canonical list via /song-requests/{eventId}/admin and writes each
 * change via the no-notify admin endpoint PUT /song-requests/{eventId}/songs.
 * Same store the client page reads/writes, so both edit one list.
 */
export default function AdminSongSections({ eventId }) {
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState('');
  const [link, setLink] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [eventName, setEventName] = useState('');
  const [songs, setSongs] = useState([]);
  const [caps, setCaps] = useState(DEFAULT_CAPS);
  const [copied, setCopied] = useState(false);
  const saveTimer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get(`/song-requests/${eventId}/admin`);
        if (cancelled) return;
        const d = res.data || {};
        setToken(d.token || '');
        setLink(d.url || d.path || '');
        setClientEmail(d.clientEmail || '');
        setEventName(d.eventName || '');
        setSongs((Array.isArray(d.songs) ? d.songs : []).map((s) => ({
          title: s.title,
          artist: s.artist || '',
          section: ['must', 'dont', 'maybe'].includes(s.section) ? s.section : 'must',
          source: s.source || undefined,
        })));
        if (d.sectionCaps) setCaps({ ...DEFAULT_CAPS, ...d.sectionCaps });
      } catch (e) {
        // leave empty — the picker still works once a token is minted on read
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [eventId]);

  // Persist to the SAME canonical store (no-notify admin endpoint), debounced
  // so a flurry of add/move taps doesn't fire a write per tap.
  const persist = useCallback((next) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await apiClient.put(`/song-requests/${eventId}/songs`, {
          songs: next.map((s) => ({ title: s.title, artist: s.artist || '', section: s.section, source: s.source || '' })),
        });
      } catch (e) {
        toast.error('Could not save songs — try again');
      }
    }, 500);
  }, [eventId]);

  useEffect(() => () => { if (saveTimer.current) clearTimeout(saveTimer.current); }, []);

  const handleChange = (next) => {
    setSongs(next);
    persist(next);
  };

  const copyLink = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
      toast.success('Client song link copied');
    } catch {
      toast.error('Could not copy');
    }
  };

  const sendToClient = () => {
    if (!clientEmail || !link) return;
    const subject = encodeURIComponent(`Your song requests${eventName ? ` — ${eventName}` : ''}`);
    const body = encodeURIComponent(
      `Hi,\n\nPlease add your song requests here:\n${link}\n\nYou can search songs, preview them, and sort them into Must Play, Do Not Play, and Play If Possible — or paste a Spotify playlist.\n\nThank you!`,
    );
    // Opens the compose draft only — never auto-sends.
    window.location.href = `mailto:${clientEmail}?subject=${subject}&body=${body}`;
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-[12px] text-white/40 py-3" data-testid="admin-song-loading">
        <Loader2 className="w-4 h-4 animate-spin text-[#D4AF37]/60" /> Loading song lists…
      </div>
    );
  }

  return (
    <div data-testid="admin-song-sections">
      {/* Client row — share the SAME link the client page uses */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <button
          type="button"
          onClick={copyLink}
          disabled={!link}
          className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12px] font-medium text-white/80 bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] transition disabled:opacity-40"
          data-testid="admin-song-copy-link"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Link2 className="w-3.5 h-3.5" />}
          {copied ? 'Copied' : 'Copy client song link'}
        </button>
        {clientEmail && (
          <button
            type="button"
            onClick={sendToClient}
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12px] font-medium text-[#D4AF37] bg-[#D4AF37]/10 border border-[#D4AF37]/30 hover:bg-[#D4AF37]/15 transition"
            data-testid="admin-song-send-client"
          >
            <Mail className="w-3.5 h-3.5" /> Send to client
          </button>
        )}
      </div>

      <SongSectionsPicker songs={songs} onChange={handleChange} caps={caps} token={token} eventTitle={eventName} />
    </div>
  );
}
